/**
 * billingService — checkout de planos/pacotes (Escopo 2, Modelo B).
 *
 * A API é o BFF: encaminha ao serviço Billing internamente. O front só conhece o slug do
 * plano (da vitrine) — a API resolve para id e deriva o cliente do JWT.
 *
 * Fluxo: createOrder → redireciona o navegador para checkoutUrl (gateway) → o gateway
 * volta para /billing/return?token=... → confirmOrder aplica a fulfillment síncrona.
 */
import api from './api';
import { sessionReplayEvent } from '../config/sessionReplayInit';

const billingService = {
  /**
   * Inicia uma compra. Devolve { intentId, orderId, checkoutUrl, pix, callbackToken, provider }.
   * No PIX, `checkoutUrl` é null e `pix` = { encodedImage, payload, expiresAtUtc } (QR embeddado).
   * `document` = CPF ou CNPJ LIMPO (utils/documento.js) e `billingName` = nome ou razão social, os dois
   * vindos da modal "Dados para o pagamento". Sem eles, a API usa os dados de cobrança salvos.
   * @param {{kind:string, planSlug?:string, planId?:string, billingCycle?:string, productId?:string, document?:string, billingName?:string, methods?:string[], providerSlug?:string}} input
   */
  async createOrder(input) {
    const res = await api.post('/v1/billing/orders', input);
    // Replay de sessão: o funil de compra por evento (reused/alreadyPaid vêm da API desde 17/09).
    sessionReplayEvent(res.data?.alreadyPaid ? 'purchase_already_paid' : (res.data?.reused ? 'purchase_reused' : 'purchase_started'), {
      kind: input?.kind ?? '', productId: input?.productId ?? '', provider: res.data?.provider ?? ''
    });
    return res.data;
  },

  /**
   * Meios de pagamento liberados para um tipo de compra (Billing:PackMethods na API).
   * Ex.: getPaymentMethods('pack') → ['Pix', 'CreditCard']. Sem gateway na resposta, de propósito:
   * quem escolhe o gateway de cada meio é o Billing (docs/PLANO-CARTAO-PACOTES.md, D6).
   * @param {string} kind
   * @returns {Promise<string[]>}
   */
  async getPaymentMethods(kind) {
    const res = await api.get('/v1/billing/methods', { params: { kind } });
    return Array.isArray(res.data?.methods) ? res.data.methods : ['Pix'];
  },

  /** Confirma no retorno do checkout. Devolve { billingOutcome, fulfillmentOutcome, externalRef, kind }. */
  async confirmOrder(callbackToken) {
    const res = await api.post('/v1/billing/orders/confirm', { callbackToken });
    return res.data;
  },

  /**
   * Vitrine: lista os pacotes visíveis de um medidor (ex.: 'whatsapp_messages').
   * Devolve [{ id, slug, name, description, creditQty, validityDays, priceCents }].
   */
  async listProducts(meter) {
    const res = await api.get('/v1/billing/products', { params: { meter } });
    return res.data;
  },
};

export default billingService;
